import type { NotificationsState } from '@api/notifications.api';
import { NOTIFICATIONS_QUERY_KEY } from '@hooks/notifications';
import { legacyFeatureFlagsStore } from '@services/featureFlags/legacyFeatureFlags.store';
import { logger } from '@services/logger/logger.service';
import {
  type RealtimeErrorsCountChanged,
  realtimeErrorsCountChanged,
} from '@store/externalEvents/externalEvents.action';
import type { QueryClient } from '@tanstack/react-query';
import { invalidateDashboardCache } from '@utils/caching.util';
import { all, getContext, takeEvery } from 'redux-saga/effects';

function receiveErrorsCountChanged() {
  return function* saga({ payload }: ReturnType<RealtimeErrorsCountChanged>) {
    const queryClient: QueryClient = yield getContext('queryClient');
    const featureFlags = legacyFeatureFlagsStore.get();

    // Feature flag check - exit early if disabled
    if (!featureFlags['enable-realtime-error-notifications']) {
      return;
    }

    try {
      const { delta, workspace_reference } = payload;

      logger.info('LOGIC', '[realtime] Errors count changed, updating cache', {
        workspace: workspace_reference,
        delta,
      });

      // Update cache directly without refetch
      yield queryClient.setQueryData<NotificationsState>([NOTIFICATIONS_QUERY_KEY], (old) => {
        if (!old) return old;

        const currentCount = old?.documents?.errors?.count || 0;
        const newCount = Math.max(0, currentCount + delta); // Ensure count never goes below 0

        return {
          ...old,
          documents: {
            ...old.documents,
            errors: {
              ...old.documents.errors,
              count: newCount,
            },
          },
        };
      });

      // Invalidate errors dashboard cache without refetching
      // Refetch will happen automatically when user navigates to dashboard
      yield invalidateDashboardCache({
        dashboardType: 'errors',
        refetchType: 'none',
        queryClient,
      });
    } catch (err) {
      logger.error(
        'LOGIC',
        '[realtime] Failed to update notifications cache for errors count change',
        err,
      );
    }
  };
}

export function* errorsCountRealtimeSagas() {
  yield all([takeEvery(realtimeErrorsCountChanged.type, receiveErrorsCountChanged())]);
}
